import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowRight, Thermometer, Brain, Heart, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function BlogPost() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Article Header */}
            <div className="mb-12">
              <div className="flex items-center space-x-2 text-blue-400 text-sm font-semibold mb-4">
                <span className="bg-blue-400/10 px-3 py-1 rounded-full">RECOVERY GUIDE</span>
              </div>
              <h1 className="text-4xl sm:text-6xl font-bold mb-6">The Science Behind Hot & Cold Therapy: A Complete Guide</h1>
              <div className="flex flex-wrap items-center gap-6 text-gray-400">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>March 20, 2024</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>5 min read</span>
                </div>
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>By Fitness Portal Team</span>
                </div>
              </div>
            </div>

            <div className="prose prose-lg prose-invert max-w-none">
              {/* Featured Image */}
              <div className="aspect-w-16 aspect-h-9 mb-12 rounded-2xl overflow-hidden">
                <img
                  src="https://i.ibb.co/Y733h978/DSC01360.jpg"
                  alt="Hot and Cold Therapy"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Introduction */}
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-8 mb-12">
                <h2 className="text-2xl font-bold mb-4 flex items-center">
                  <Brain className="w-6 h-6 mr-2 text-blue-400" />
                  Understanding Contrast Therapy
                </h2>
                <p className="text-gray-300 mb-4">
                  Contrast therapy, alternating between hot and cold temperatures, has been a cornerstone of recovery and wellness for centuries. From ancient Roman baths to modern athletic recovery protocols, this method creates a powerful pumping effect in your body that accelerates recovery and provides numerous health benefits. At Fitness Portal, we've integrated this time-tested approach with modern technology to optimize your recovery experience.
                </p>
                <p className="text-gray-300">
                  The combination of hot and cold exposure creates a unique physiological response that's more powerful than either therapy alone. This synergistic effect enhances recovery, boosts performance, and promotes overall well-being through multiple biological pathways.
                </p>
              </div>

              {/* Cold Therapy Section */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 flex items-center">
                  <Thermometer className="w-8 h-8 mr-3 text-blue-400" />
                  The Science of Cold Therapy
                </h2>
                <p className="mb-6">
                  Our ice baths, maintained at a precise 4°C (39°F), trigger several crucial physiological responses that enhance recovery and performance:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white/5 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Physiological Responses</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Vasoconstriction of blood vessels</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Reduced metabolic activity</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Decreased nerve conduction velocity</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Increased norepinephrine release</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Enhanced lymphatic drainage</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white/5 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Benefits</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Reduced inflammation and swelling</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Enhanced immune system function</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Improved mental resilience</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Accelerated metabolic rate</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Better sleep quality</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Heat Therapy Section */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 flex items-center">
                  <Heart className="w-8 h-8 mr-3 text-blue-400" />
                  Benefits of Heat Therapy
                </h2>
                <p className="mb-6">
                  Our traditional sauna, operating at 80-90°C (176-194°F), provides a comprehensive range of therapeutic benefits through deep tissue heating and physiological adaptations:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white/5 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Immediate Effects</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Increased blood flow and circulation</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Deep muscle relaxation</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Enhanced flexibility and mobility</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Increased growth hormone release</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Improved nutrient delivery</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white/5 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Long-term Benefits</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Enhanced cardiovascular health</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Improved detoxification</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Reduced chronic stress</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Better skin health</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Increased stress resilience</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Combined Benefits Section - NEW */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6">The Power of Combining Hot & Cold</h2>
                <p className="mb-6">
                  When alternating between hot and cold therapies, you create a powerful synergistic effect that maximizes the benefits of both modalities. This combination triggers unique physiological responses that enhance recovery and performance:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Circulatory Benefits</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Enhanced blood flow</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Improved lymphatic drainage</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Better nutrient delivery</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Faster waste removal</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Recovery Benefits</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Reduced muscle soreness</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Faster recovery time</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Decreased inflammation</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Enhanced tissue repair</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Performance Benefits</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Improved endurance</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Better stress adaptation</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Enhanced mental focus</span>
                      </li>
                      <li className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                        <span>Increased resilience</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Optimal Protocol */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Recommended Protocol</h2>
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-8">
                  <h3 className="text-xl font-bold mb-4">For optimal results, follow these guidelines:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-bold mb-3">Basic Protocol</h4>
                      <ol className="space-y-3">
                        <li className="flex items-center space-x-3">
                          <span className="bg-blue-400/20 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">1</span>
                          <span>5-10 minutes in sauna (80-90°C)</span>
                        </li>
                        <li className="flex items-center space-x-3">
                          <span className="bg-blue-400/20 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">2</span>
                          <span>1-3 minutes in ice bath (4°C)</span>
                        </li>
                        <li className="flex items-center space-x-3">
                          <span className="bg-blue-400/20 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">3</span>
                          <span>Repeat cycle 3-4 times</span>
                        </li>
                        <li className="flex items-center space-x-3">
                          <span className="bg-blue-400/20 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">4</span>
                          <span>Always finish with cold exposure</span>
                        </li>
                      </ol>
                    </div>
                    <div>
                      <h4 className="font-bold mb-3">Key Considerations</h4>
                      <ul className="space-y-3">
                        <li className="flex items-center space-x-3">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                          <span>Stay hydrated throughout</span>
                        </li>
                        <li className="flex items-center space-x-3">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                          <span>Listen to your body</span>
                        </li>
                        <li className="flex items-center space-x-3">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                          <span>Breathe steadily and remain calm</span>
                        </li>
                        <li className="flex items-center space-x-3">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                          <span>Build tolerance gradually</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Safety Considerations */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold mb-6 flex items-center">
                  <Shield className="w-8 h-8 mr-3 text-blue-400" />
                  Safety Considerations
                </h2>
                <div className="bg-white/5 rounded-xl p-6">
                  <p className="mb-4">
                    While contrast therapy is generally safe, consult your healthcare provider before starting if you have:
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <li className="flex items-center space-x-3">
                      <div className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                      <span>Cardiovascular conditions</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                      <span>Pregnancy</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                      <span>High blood pressure</span>
                    </li>
                    <li className="flex items-center space-x-3">
                      <div className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                      <span>Recent injuries or surgeries</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Call to Action */}
              <section>
                <div className="bg-gradient-to-r from-blue-600/30 to-cyan-600/30 rounded-2xl p-8">
                  <h2 className="text-2xl font-bold mb-4">Experience It Yourself</h2>
                  <p className="text-gray-300 mb-6">
                    Visit our Recovery Zone to experience the benefits of contrast therapy. Our facilities include professional ice baths, traditional saunas, and expert guidance to ensure a safe and effective recovery session.
                  </p>
                  <a
                    href="/recovery"
                    className="inline-flex items-center space-x-2 bg-white text-black px-6 py-3 rounded-full font-bold hover:bg-gray-200 transition-colors"
                  >
                    <span>Explore Our Recovery Zone</span>
                    <ArrowRight className="w-5 h-5" />
                  </a>
                </div>
              </section>
            </div>
          </motion.div>
        </article>
      </main>

      <Footer />
    </div>
  );
}